import { AiUsageLimitEventAdapter } from '~analytics/adapters/AiUsageLimitEventAdapter';
import { translate } from '~l10n/client';
import { toast } from '~shared/components/Toast';

import { isAxiosError } from '../../../../api/isAxiosError';

type AiRateLimitType = 'global_limit' | 'per_day' | 'per_minute';

export function getAiRateLimitType(
  responseData: RateLimitError
): AiRateLimitType {
  if (isGlobalRateLimitError(responseData)) {
    return 'global_limit';
  }
  if (isPerDayRateLimitExceeded(responseData)) {
    return 'per_day';
  }
  if (isMinuteRateLimitExceeded(responseData)) {
    return 'per_minute';
  }
  return undefined;
}

export function handleAiRateLimitError(error: unknown) {
  if (!isAxiosError(error)) {
    return;
  }

  if (
    error.response?.status === 429 &&
    isRateLimitError(error.response?.data)
  ) {
    const responseData = error.response.data;

    const aiRateLimitType = getAiRateLimitType(responseData);

    switch (aiRateLimitType) {
      case 'global_limit': {
        toast.critical(
          translate('Beta feature is at capacity. Please try again later.')
        );

        // Analytics
        new AiUsageLimitEventAdapter({
          limitType: 'global_limit',
        }).sendAnalytics();

        break;
      }
      case 'per_day': {
        toast.critical(
          translate('You’ve reached the daily limit for this beta.')
        );

        // Analytics
        new AiUsageLimitEventAdapter({
          limitType: 'per_day',
        }).sendAnalytics();
        break;
      }

      case 'per_minute': {
        toast.critical(
          translate('Rate Limit Exceeded. Wait a few moments and try again.')
        );

        // Analytics
        new AiUsageLimitEventAdapter({
          limitType: 'per_minute',
        }).sendAnalytics();
        break;
      }
    }
  }
}

type MinuteRateLimitExceeded = {
  error: 'rate_limit_exceeded';
};

type PerDayRateLimitExceeded = {
  error: 'rate_limit_exceeded';
  expiry: number;
};

type GlobalRateLimitExceeded = {
  error: 'global_rate_limit_exceeded';
};

type RateLimitError =
  | MinuteRateLimitExceeded
  | PerDayRateLimitExceeded
  | GlobalRateLimitExceeded;

export function isRateLimitError(error: any): error is RateLimitError {
  return (error as RateLimitError).error !== undefined;
}

function isGlobalRateLimitError(
  response: RateLimitError
): response is GlobalRateLimitExceeded {
  return response.error === 'global_rate_limit_exceeded';
}

function isPerDayRateLimitExceeded(
  response: RateLimitError
): response is MinuteRateLimitExceeded {
  return (
    response.error === 'rate_limit_exceeded' &&
    (response as any).expiry !== undefined
  );
}

function isMinuteRateLimitExceeded(
  response: RateLimitError
): response is PerDayRateLimitExceeded {
  return (
    response.error === 'rate_limit_exceeded' &&
    (response as any).expiry === undefined
  );
}
